'use strict';

var dayjs = require('dayjs');
var calendar = require('dayjs/plugin/calendar');
var customParseFormat = require('dayjs/plugin/customParseFormat');
var duration = require('dayjs/plugin/duration');
var isBetween = require('dayjs/plugin/isBetween');
var localizedFormat = require('dayjs/plugin/localizedFormat');
var minMax = require('dayjs/plugin/minMax');
var objectSupport = require('dayjs/plugin/objectSupport');
var quarterOfYear = require('dayjs/plugin/quarterOfYear');
var relativeTime = require('dayjs/plugin/relativeTime');
var timezone = require('dayjs/plugin/timezone');
var updateLocale = require('dayjs/plugin/updateLocale');
var utc = require('dayjs/plugin/utc');
require('dayjs/locale/en');
require('dayjs/locale/nl');

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(localizedFormat);
dayjs.extend(minMax);
dayjs.extend(relativeTime);
dayjs.extend(calendar);
dayjs.extend(isBetween);
dayjs.extend(objectSupport);
dayjs.extend(quarterOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(updateLocale);
dayjs.updateLocale('nl', {
    calendar: {
        lastDay: '[gisteren om] LT',
        lastWeek: '[afgelopen] dddd [om] LT',
        nextDay: '[morgen om] LT',
        nextWeek: 'dddd [om] LT',
        sameDay: '[vandaag om] LT',
        sameElse: 'l',
    },
});

/* eslint-disable typescript-sort-keys/interface */
/**
 * Class representing a date/time. Currently uses dayjs as underlying system.
 * Please refer to the dayjs documentation for specifics on things like parsing and formatting.
 */
class DateTime {
    constructor(date, timezone) {
        let referenceValue = this.getReferenceValue(date);
        // NOTE: This check can possibly be removed when https://github.com/iamkun/dayjs/pull/1963/files
        // is merged and released.
        if (referenceValue === null) {
            referenceValue = 'Invalid Date';
        }
        this._value = timezone ? dayjs.tz(referenceValue, timezone) : dayjs(referenceValue);
    }
    /**
     * Creates a new immutable `DateTime` instance for the current date and time.
     */
    static now() {
        return new DateTime();
    }
    /**
     * Creates a new immutable `DateTime` instance for the current UTC date and time.
     */
    static utc() {
        return new DateTime(dayjs.utc());
    }
    /**
     * Creates a new immutable `DateTime` instance for the current date.
     */
    static today() {
        return DateTime.now().startOf('day');
    }
    /**
     * Creates a new immutable `DateTime` instance for the specified input.
     * @param date The input date.
     * @param timezone The timezone.
     */
    static from(date, timezone) {
        return new DateTime(date, timezone);
    }
    /**
     * Creates a new immutable `DateTime` instance based on the input unix timestamp.
     * @param unix The unix timestamp.
     */
    static fromUnix(unix) {
        return new DateTime(dayjs.unix(unix));
    }
    /**
     * Creates a new immutable `DateTime` instance based on the input object.
     * @param object Input object.
     */
    static fromObject(object) {
        return new DateTime(dayjs(object));
    }
    /**
     * Creates a new immutable `DateTime` instance by parsing the input string.
     * @param date Input string to parse.
     * @param format Format string.
     * @param locale The locale.
     */
    static parse(date, format, locale) {
        return new DateTime(dayjs(date, format, locale, false));
    }
    /**
     * Creates a new immutable `DateTime` instance by strict parsing the input string.
     * @param date Input string to parse.
     * @param format Format string.
     * @param locale The locale.
     */
    static parseExact(date, format, locale) {
        return new DateTime(dayjs(date, format, locale, true));
    }
    /**
     * Creates a new immutable UTC `DateTime` instance by parsing the input string.
     * @param date Input string to parse.
     * @param format Format string.
     */
    static parseUtc(date, format) {
        return new DateTime(dayjs.utc(date, format, false));
    }
    /**
     * Creates a new immutable UTC `DateTime` instance by strict parsing the input string.
     * @param date Input string to parse.
     * @param format Format string.
     */
    static parseUtcExact(date, format) {
        return new DateTime(dayjs.utc(date, format, true));
    }
    /**
     * Returns a new immutable `DateTime` instance of the minimum DateTime from the input array.
     * @param dates DateTime array.
     */
    static min(dates) {
        const minDate = dayjs.min(dates.map((date) => date._value));
        return minDate ? new DateTime(minDate) : undefined;
    }
    /**
     * Returns a new immutable `DateTime` instance of the maximum DateTime from the input array.
     * @param dates DateTime array.
     */
    static max(dates) {
        const maxDate = dayjs.max(dates.map((date) => date._value));
        return maxDate ? new DateTime(maxDate) : undefined;
    }
    /**
     * Set the default locale to be used.
     */
    static setDefaultLocale(locale) {
        dayjs.locale(locale);
    }
    year(year) {
        if (year !== undefined) {
            return new DateTime(this._value.year(year));
        }
        else {
            return this._value.year();
        }
    }
    month(month) {
        if (month !== undefined) {
            return new DateTime(this._value.month(month));
        }
        else {
            return this._value.month();
        }
    }
    date(date) {
        if (date !== undefined) {
            return new DateTime(this._value.date(date));
        }
        else {
            return this._value.date();
        }
    }
    hour(hour) {
        if (hour !== undefined) {
            return new DateTime(this._value.hour(hour));
        }
        else {
            return this._value.hour();
        }
    }
    minute(minute) {
        if (minute !== undefined) {
            return new DateTime(this._value.minute(minute));
        }
        else {
            return this._value.minute();
        }
    }
    second(second) {
        if (second !== undefined) {
            return new DateTime(this._value.second(second));
        }
        else {
            return this._value.second();
        }
    }
    millisecond(millisecond) {
        if (millisecond !== undefined) {
            return new DateTime(this._value.millisecond(millisecond));
        }
        else {
            return this._value.millisecond();
        }
    }
    day(day) {
        if (day !== undefined) {
            return new DateTime(this._value.day(day));
        }
        else {
            return this._value.day();
        }
    }
    quarter(quarter) {
        if (quarter !== undefined) {
            return new DateTime(this._value.quarter(quarter));
        }
        else {
            return this._value.quarter();
        }
    }
    /**
     * Returns the unix timestamps for this DateTime instance.
     */
    unix() {
        return this._value.unix();
    }
    /**
     * Returns the UTC offset for this DateTime instance.
     */
    utcOffset() {
        return this._value.utcOffset();
    }
    /**
     * Returns the string representation for this DateTime instance.
     */
    toString() {
        return this._value.toString();
    }
    /**
     * Returns the Date representation for this DateTime instance.
     */
    toDate() {
        return this._value.toDate();
    }
    /**
     * Format this DateTime instance to a string.
     * @param template Formatting template. Please refer to dayjs documentation for a detailed reference.
     */
    format(template) {
        return this._value.format(template);
    }
    /**
     * Returns the string of relative time from now. E.g. 'in an hour'.
     * @param withoutSuffix When set to `true`, leaves out the suffix/prefix, e.g. 'an hour' instead of 'in an hour'.
     */
    formatFromNow(withoutSuffix) {
        return this._value.fromNow(withoutSuffix);
    }
    /**
     * Returns the string of relative time from now as a calendar representation. E.g. 'Today at 1:00 PM'.
     * @param referenceDate Use this reference date instead of current date/time.
     * @param formats Calendar formats to use. Please refer to dayjs documentation for a detailed reference.
     */
    formatFromNowAsCalendar(referenceDate, formats) {
        const referenceValue = referenceDate ? this.getReferenceValue(referenceDate) : null;
        return this._value.calendar(referenceValue, formats);
    }
    /**
     * Returns the ISO 8601 string representation for this DateTime instance.
     */
    toISOString() {
        return this._value.toISOString();
    }
    /**
     * Returns a new immutable `DateTime` instance, converted to another timezone.
     * @param timezone The timezone.
     * @param keepLocalTime Treat existing time as local time.
     */
    tz(timezone, keepLocalTime) {
        return new DateTime(this._value.tz(timezone, keepLocalTime));
    }
    /**
     * Gets or sets the current locale.
     */
    locale(language) {
        if (language !== undefined) {
            return new DateTime(this._value.locale(language));
        }
        else {
            return this._value.locale();
        }
    }
    /**
     * Returns a cloned `DateTime` instance with a specified amount of time added.
     * @param value Value to add.
     * @param unit Unit of time to use.
     */
    add(value, unit) {
        return new DateTime(this._value.add(value, unit));
    }
    /**
     * Returns a cloned `DateTime` instance with a specified amount of time subtracted.
     * @param value Value to subtract.
     * @param unit Unit of time to use.
     */
    subtract(value, unit) {
        return new DateTime(this._value.subtract(value, unit));
    }
    /**
     * Returns a cloned `DateTime` instance and set it to the start of a unit of time.
     * @param unit Unit of time to use.
     */
    startOf(unit) {
        return new DateTime(this._value.startOf(unit));
    }
    /**
     * Returns a cloned `DateTime` instance and set it to the end of a unit of time.
     * @param unit Unit of time to use.
     */
    endOf(unit) {
        return new DateTime(this._value.endOf(unit));
    }
    /**
     * Returns a `boolean` indicating whether this instance is equal to the given `DateTimeLike` input.
     * @param date `DateTimeLike` input to compare to.
     * @param unit Optional unit of time to compare.
     */
    isSame(date, unit) {
        return this._value.isSame(this.getReferenceValue(date), unit);
    }
    /**
     * Returns a `boolean` indicating whether this instance is before the given `DateTimeLike` input.
     * @param date `DateTimeLike` input to compare to.
     * @param unit Optional unit of time to compare.
     */
    isBefore(date, unit) {
        return this._value.isBefore(this.getReferenceValue(date), unit);
    }
    /**
     * Returns a `boolean` indicating whether this instance is after the given `DateTimeLike` input.
     * @param date `DateTimeLike` input to compare to.
     * @param unit Optional unit of time to compare.
     */
    isAfter(date, unit) {
        return this._value.isAfter(this.getReferenceValue(date), unit);
    }
    /**
     * Returns a `boolean` indicating whether this instance is the same or before the given `DateTimeLike` input.
     * @param date `DateTimeLike` input to compare to.
     * @param unit Optional unit of time to compare.
     */
    isSameOrBefore(date, unit) {
        const referenceValue = this.getReferenceValue(date);
        return this._value.isSame(referenceValue, unit) || this._value.isBefore(referenceValue, unit);
    }
    /**
     * Returns a `boolean` indicating whether this instance is the same or after the given `DateTimeLike` input.
     * @param date `DateTimeLike` input to compare to.
     * @param unit Optional unit of time to compare.
     */
    isSameOrAfter(date, unit) {
        const referenceValue = this.getReferenceValue(date);
        return this._value.isSame(referenceValue, unit) || this._value.isAfter(referenceValue, unit);
    }
    /**
     * Returns a `boolean` indicating whether this instance is the between the given `DateTimeLike` inputs.
     * @param startDate `DateTimeLike` lower bound of the range.
     * @param endDate `DateTimeLike` upper bound of the range.
     * @param unit Optional unit of time to compare.
     * @param bounds Bounds inclusion/exclusion option.
     */
    isBetween(startDate, endDate, unit, bounds) {
        const startDateReferenceValue = this.getReferenceValue(startDate);
        const endDateReferenceValue = this.getReferenceValue(endDate);
        return this._value.isBetween(startDateReferenceValue, endDateReferenceValue, unit, bounds);
    }
    /**
     * Returns the difference with the given date. By default, the difference is returned in milliseconds.
     * @param date Date to compare to.
     * @param unit Optional unit of time to return.
     */
    diff(date, unit) {
        return this._value.diff(this.getReferenceValue(date), unit);
    }
    /**
     * Returns a `boolean` indicating whether this `DateTime` instance falls within a weekend.
     */
    isWeekend() {
        return this.day() === 6 || this.day() === 0;
    }
    /**
     * Returns a `boolean` indicating whether this `DateTime` instance is valid.
     */
    isValid() {
        return this._value.isValid();
    }
    getReferenceValue(date) {
        return this.isDateTime(date) ? date._value : date;
    }
    isDateTime(date) {
        return (date === null || date === void 0 ? void 0 : date._value) !== undefined;
    }
}

/**
 * Class representing a duration. Currently uses dayjs as underlying system.
 * Please refer to the dayjs documentation for specifics on things like formatting.
 */
class Duration {
    constructor(value, unit) {
        this._value = dayjs.duration(value, unit);
    }
    /**
     * Returns a new `Duration` instance based on the given input parameters.
     * @param value The duration value.
     * @param unit Unit of time to use.
     */
    static from(value, unit = 'milliseconds') {
        return new Duration(value, unit);
    }
    /**
     * Formats this `Duration` instance to a string.
     * @param template Templating format.  Please refer to dayjs documentation for a detailed reference.
     */
    format(template) {
        return this._value.format(template);
    }
    /**
     * Formats this `Duration` instance to a string with the 'HH:mm:ss' template,
     * allowing for the hours component to exceed 24 hours, which is not supported by the format() function.
     */
    formatHHmmssAbove24Hours() {
        return `${Math.floor(this.asHours())}:${this.format('mm:ss')}`;
    }
    /**
     * Returns this duration as milliseconds.
     */
    asMilliseconds() {
        return this._value.asMilliseconds();
    }
    /**
     * Returns this duration as seconds.
     */
    asSeconds() {
        return this._value.asSeconds();
    }
    /**
     * Returns this duration as minutes.
     */
    asMinutes() {
        return this._value.asMinutes();
    }
    /**
     * Returns this duration as hours.
     */
    asHours() {
        return this._value.asHours();
    }
    /**
     * Returns this duration as days.
     */
    asDays() {
        return this._value.asDays();
    }
    /**
     * Returns this duration as weeks.
     */
    asWeeks() {
        return this._value.asWeeks();
    }
    /**
     * Returns this duration as months.
     */
    asMonths() {
        return this._value.asMonths();
    }
    /**
     * Returns this duration as years.
     */
    asYears() {
        return this._value.asYears();
    }
}

/**
 * Class representing a date range.
 */
class DateRange {
    /**
     * Constructs a new `DateRange` instance.
     * @param start Start date of the range.
     * @param end End date of the range.
     */
    constructor(start, end = null) {
        this.start = start;
        this.end = end;
    }
    /**
     * Returns a `boolean` indicating whether there is any overlap.
     * When an end date is `null`, `2900-01-01` will be used as default end date.
     * @param other Other `DateRange` to check the overlap with.
     * @returns
     */
    hasOverlapWith(other) {
        var _a, _b;
        return (this.start.isSameOrBefore((_a = other.end) !== null && _a !== void 0 ? _a : DateRange.endOfDays) &&
            ((_b = this.end) !== null && _b !== void 0 ? _b : DateRange.endOfDays).isSameOrAfter(other.start));
    }
}
DateRange.endOfDays = DateTime.from('2900-01-01');

exports.DateRange = DateRange;
exports.DateTime = DateTime;
exports.Duration = Duration;
